import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../components/ToastContext";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import GitStatusCell from "./GitStatus";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { useConfig } from "../../services/ConfigContext";

import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  MenuItem,
  TextField,
  Button,
  Divider,
  Chip,
  Skeleton,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  LockOutlined,
  InfoOutlined,
  ErrorOutlineOutlined,
  FilterListRounded,
  LocalOfferOutlined,
  Circle,
  CodeOutlined,
} from "@mui/icons-material";
import { api } from "../../services/api";
import VMResizeHistoryTable from "./VMResizeHistoryTable";
import RoleGuard from "../../components/RoleGuard";


const InstanceResize = ({ resourceOverview, resourceTypeId, resourceId }) => {
  const [instanceTypes, setInstanceTypes] = useState([]);
  const [selectedInstance, setSelectedInstance] = useState("");
  const [pricing, setPricing] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const toast = useToast();

  const [resizeHistory, setResizeHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();
    const { config} = useConfig();
    const MonthlyPricingCalculating = config?.MonthlyHourPricingCalculating;


  const [operationDetails, setOperationDetails] = useState(null);
  const [maintenanceWindow, setMaintenanceWindow] = useState("");

  const [openValidationDialog, setOpenValidationDialog] = useState(false);
  const [validationReason, setValidationReason] = useState("");
  const [validationFields, setValidationFields] = useState([]);
  const [dateError, setDateError] = useState(false);

  const canResizeInstance =
    ["running", "stopped"].includes(
      resourceOverview?.resourceStatus?.toLowerCase()
    ) &&
    !!resourceOverview?.awsInstanceId;

  const isEmptyResizeState =
    !canResizeInstance && resizeHistory.length === 0;

  useEffect(() => {
    const loadOperationDetails = async () => {
      try {
        const cloudProviderId =
          localStorage.getItem("selectedProviderId");

        const response = await api.get(
          `/api/Application/popularServices?cloudProviderId=${cloudProviderId}&serviceId=2&page=1&pageSize=10`
        );

        const vmResizeOperation = response.data?.find(
          (item) => item.resourceName === "VM Resize"
        );

        setOperationDetails(vmResizeOperation);
      } catch (error) {
        console.error("Failed to load VM Resize operation", error);
      }
    };

    loadOperationDetails();
  }, []);

  // Dynamic helper extractor for resource fields
  const getFieldValue = (fieldName) => {
    const field = resourceOverview?.resourceRequestDetails?.find(
      (x) => x.fieldName === fieldName
    );
    return field?.fieldData || "—";
  };

  const getFieldRawValue = (fieldName) => {
    const field = resourceOverview?.resourceRequestDetails?.find(
      (x) => x.fieldName === fieldName
    );
    return field?.fieldValue;
  };

  const region = getFieldValue("location") !== "—" ? getFieldValue("location") : "us-east-1";
  const currentInstanceType =
    getFieldValue("Ec2instance") !== "—" ? getFieldValue("Ec2instance") : "t1.micro";
  const currentCpu =
    getFieldValue("vCPU") !== "—"
      ? getFieldValue("vCPU")
      : getFieldValue("cpu") !== "—"
        ? getFieldValue("cpu")
        : "1";
  const currentRam =
    getFieldValue("RAM") !== "—"
      ? getFieldValue("RAM")
      : getFieldValue("memory") !== "—"
        ? getFieldValue("memory")
        : "1 GiB";
  const platform =
    getFieldValue("platformDetails")
  const status =
    resourceOverview?.resourceStatus === null ||
      resourceOverview?.resourceStatus?.trim() === ""
      ? "Not Deployed"
      : resourceOverview?.resourceStatus;

  // API 3 Specs: New Flags & Prices
  const isActiveVMRequest = Boolean(resourceOverview?.isActiveVMRequest);
  const currentMonthlyPrice = parseFloat(
    resourceOverview?.resourceTotalPrice ?? 0
  );
  const currentMarketPrice = parseFloat(
    resourceOverview?.resourceMarketPrice ?? 0
  );
  // const currentHourlyPrice = (currentMonthlyPrice / 730).toFixed(3);
  const currentHourlyPrice = (Math.floor((currentMonthlyPrice /MonthlyPricingCalculating) * 1000) / 1000).toFixed(3);

  const statusConfig = {
    running: {
      bg: "#dcfce7",
      text: "#166534",
      dot: "#16a34a",
    },
    stopped: {
      bg: "#fee2e2",
      text: "#991b1b",
      dot: "#dc2626",
    },
  };

  const currentStatus =
    statusConfig[status?.toLowerCase()] || {
      bg: "#fef3c7",
      text: "#92400e",
      dot: "#f59e0b",
    };

  // 1. Fetch Available Instance Types
  useEffect(() => {
    if (!region || region === "—") return;
    const loadInstanceTypes = async () => {
      setLoadingTypes(true);
      try {
        const response = await api.get(`/api/AwsEc2/AwsInstanceTypes/${region}`);
        setInstanceTypes(response.data || []);
      } catch (error) {
        console.error("Failed to load instance types", error);
      } finally {
        setLoadingTypes(false);
      }
    };
    loadInstanceTypes();
  }, [region]);

  // 2. Fetch Pricing
  useEffect(() => {
    if (!selectedInstance) return;
    const getPricing = async () => {
      try {
        setLoadingPricing(true);
        const organizationId = sessionStorage.getItem("organizationId");
        const cloudProviderId = localStorage.getItem("selectedProviderId");
        const response = await api.get(
          `/api/AwsEc2/pricing/${organizationId}/${cloudProviderId}/${selectedInstance}/${encodeURIComponent(
            platform
          )}/${region}/${resourceTypeId}`
        );
        setPricing(response.data);
        const pricingData = response.data;
        if (
          !pricingData ||
          Number(pricingData?.pricePerMonth || 0) === 0
        ) {
          setValidationReason(
            "The selected instance type has no pricing information available. Please select a different instance type to continue."
          );
          setOpenValidationDialog(true);
        }
      } catch (error) {
        console.error("Pricing Error", error);
      } finally {
        setLoadingPricing(false);
      }
    };
    getPricing();
  }, [selectedInstance, region, platform, resourceTypeId]);

  // 3. Fetch History (API Endpoint 2)
  const fetchHistory = useCallback(async () => {
    const targetId = resourceId || resourceOverview?.resource_Id;

    if (!targetId) return;
    setLoadingHistory(true);
    try {
      const response = await api.get(
        `/api/Resources/GetVMResizeHistory/${targetId}`
      );
      setResizeHistory(response.data || []);
    } catch (error) {
      console.error("Failed to load resize history", error);
    } finally {
      setLoadingHistory(false);
    }
  }, [resourceId, resourceOverview]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);


  const selectedInstanceObj = instanceTypes.find(
    (item) => String(item.value) === String(selectedInstance)
  );

  const selectedInstanceType =
    selectedInstanceObj?.text?.split("|")[0]?.trim();

  const isSameInstance =
    selectedInstanceType?.toLowerCase?.() ===
    currentInstanceType?.toLowerCase?.();

  // Calculated Prices
  const newMonthlyPrice = parseFloat(
    pricing?.discountPricePerMonth 
  );
  const newMonthlymarketprice = parseFloat(
    pricing?.pricePerMonth 
  );
  const newPrice = parseFloat(
    pricing?.pricePerMonth || 0
  );

  const hasNoPricing =
    selectedInstance &&
    !loadingPricing &&
    Number(pricing?.pricePerMonth ?? 0) <= 0;

  const newHourlyPrice = parseFloat(
    pricing?.pricePerHr 
  );
  // const priceDiff = newMonthlyPrice - currentMonthlyPrice;
  const priceDiff = newMonthlymarketprice - currentMonthlyPrice;


  const deltaLabel =
    priceDiff > 0
      ? "Delta Cost (Increase)"
      : priceDiff < 0
        ? "Delta Cost (Decrease)"
        : "Delta Cost";

  const deltaColor =
    priceDiff > 0
      ? "#dc2626" // red
      : priceDiff < 0
        ? "#16a34a" // green
        : "#64748b"; // gray
  // color={priceDiff > 0 ? "#fa1010" : "#16b44a"}

  const validateVmResize = async () => {
    const accountId = localStorage.getItem("selectedAccountId");

    const validationParams = {
      subnetId: Number(getFieldRawValue("subnet")),
      instanceTypeId: Number(selectedInstance), // Target instance type
      awsAmiId: getFieldValue("AwsAmis"),
      accountId,
      resourceName: getFieldValue("resourceName"),
      resourceId: Number(resourceId || resourceOverview?.resource_Id),
      isVMResizeRequest:true,
      resourceType: "EC2"
    };

    const { data } = await api.get(
      "/api/AwsEc2Data/ValidateResourceCreation",
      {
        params: validationParams,
        headers: {
          "X-CORRELATION-ID": crypto.randomUUID(),
          accept: "application/json",
        },
      }
    );

    if (!data?.isValid) {
      const err = new Error(data?.reason || "Validation failed.");
      err.showDialog = true;
      err.fieldName = data?.fieldName || "";
      throw err;
    }
  };

  // 4. Save VM Resize Request (API Endpoint 1)
  const handleSave = async () => {

    if (!canResizeInstance || !selectedInstance || isSameInstance || isActiveVMRequest) return;
    setIsSaving(true);
    try {
      await validateVmResize();
      const payload = {
        applicationName: String(resourceOverview?.applicationName),
        subscription: localStorage.getItem("selectedAccountId"),
        totalPrice: newMonthlyPrice.toString(),
        resourceRequest: JSON.stringify({ Ec2instance: selectedInstance }),
        requester: sessionStorage.getItem('userId'),
        resourceType: resourceTypeId,
        organizationId: Number(sessionStorage.getItem("organizationId")),
        parent_Resource_ID: Number(resourceId || resourceOverview?.resource_Id),
        region: region,
        cloudProvider: String(localStorage.getItem("selectedProviderId") || "2"),
        marketPrice: (pricing?.pricePerMonth ?? currentMarketPrice ?? 0).toString(),
        operationId: operationDetails?.id || 83,
        operationName: operationDetails?.resourceName || "VM Resize",
        isApprovedRequest: "true",
        applicationId: getFieldValue("applicationId"),
        environment: getFieldValue("environment"),
        resourceNameConstraint: getFieldValue("resourceName"),
        scheduledOn: maintenanceWindow ? new Date(maintenanceWindow).toISOString() : new Date().toISOString(),
        priority: "4",
        isVMResizeRequest:true,
      };

      // await api.post("/api/Resources/CreateVMResizeRequest", payload);
      const response = await api.post("/api/Resources/CreateVMResizeRequest", payload);

      toast.success(response?.data?.message ||
        "VM Resize request submitted successfully."
      )

      navigate("/provision-status");
      fetchHistory();
    } catch (error) {
      console.error("Submit Resize Error", error);

      if (error.showDialog) {
        setValidationReason(error.message);

        setValidationFields(
          error.fieldName
            ? error.fieldName.split(",").map(x => x.trim())
            : []
        );

        setOpenValidationDialog(true);
        return;
      }

      toast.error(
        error?.message || "Failed to submit VM Resize request."
      );
    } finally {
      setIsSaving(false);
    }
  };

  // const getMinDateTime = () => {
  //   const now = new Date();
  //   now.setMinutes(0, 0, 0);
  //   return now;
  // };


  const getMinDateTime = () => {
    return new Date();
  };

  // Styling
  const cardStyle = {
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    boxShadow: "none",
  };
  const labelStyle = {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#64748b",
    // textTransform: "uppercase",
    letterSpacing: "0.5px",
    mb: 0.5,
  };
  const valueStyle = { fontSize: "0.875rem", fontWeight: 600, color: "#0f172a" };

  const formattedStatus = status
  ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
  : "";
  return (
    <Box>
      {isActiveVMRequest && (
        <Box
          sx={{
            bgcolor: "#fff7ed",
            color: "#c2410c",
            p: 1.5,
            borderRadius: 2,
            mb: 2,
            display: "flex",
            gap: 1.5,
            border: "1px solid #fdba74",
            alignItems: "center",
          }}
        >
          <ErrorOutlineOutlined fontSize="small" />
          <Typography variant="body2" fontSize="0.85rem" fontWeight="500">
            A VM Resize request is already in progress for this instance. Please
            wait until the existing request is completed before submitting another
            resize request.
          </Typography>
        </Box>
      )}

      {canResizeInstance && (
        <>
          {/* 1. INSTANCE OVERVIEW */}
          <Card sx={{ border: "none", boxShadow: "none", p: 0, }}>
            <CardContent sx={{ p: 0 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h6" fontWeight="700" color="#0f172a">
                    Instance Overview
                  </Typography>
                </Box>
                <Chip
                  icon={
                    <Circle
                      sx={{
                        fontSize: "8px !important",
                        color: currentStatus.dot,
                      }}
                    />
                  }
                  label={formattedStatus}
                  size="small"
                  sx={{
                    bgcolor: currentStatus.bg,
                    color: currentStatus.text,
                    fontWeight: 600,
                    borderRadius: "6px",
                  }}
                />
              </Box>

              <Box
                mt={1.5}
                sx={{
                  bgcolor: "#f8fafc",
                  borderRadius: 2,
                  px: 2, py: 1.5
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid
                    size={{ xs: 6, sm: 2 }}
                    sx={{ borderRight: { sm: "1px solid #cbd5e1" }, pr: 2 }}
                  >
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600, mb: 0.5 }}>
                      Region
                    </Typography>
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#0f172a" }}>
                      {region}
                    </Typography>
                  </Grid>

                  {/* Platform */}
                  <Grid
                    size={{ xs: 6, sm: 2 }}
                    sx={{ borderRight: { md: "1px solid #cbd5e1" }, px: { sm: 2 } }}
                  >
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600, mb: 0.5 }}>
                      Platform
                    </Typography>
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#0f172a" }}>
                      {platform}
                    </Typography>
                  </Grid>

                  {/* AWS Instance Id */}
                  <Grid
                    size={{ xs: 12, md: 2 }}
                    sx={{ borderRight: { md: "1px solid #cbd5e1" }, px: { md: 2 } }}
                  >
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600, mb: 0.5 }}>
                      AWS Instance Id
                    </Typography>
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#0f172a", wordBreak: "break-all" }}>
                      {resourceOverview?.awsInstanceId || "—"}
                    </Typography>
                  </Grid>

                  {/* AwsAmis */}
                  <Grid
                    size={{ xs: 12, md: 2 }}
                    sx={{ borderRight: { md: "1px solid #cbd5e1" }, px: { md: 2 } }}
                  >
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600, mb: 0.5 }}>
                      AwsAmis
                    </Typography>
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#0f172a", wordBreak: "break-all" }}>
                      {getFieldValue("AwsAmis")}
                    </Typography>
                  </Grid>

                  {/* Vpc Id */}
                  <Grid
                    size={{ xs: 12, md: 2 }}
                    sx={{ borderRight: { md: "1px solid #cbd5e1" }, px: { md: 2 } }}
                  >
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600, mb: 0.5 }}>
                      Vpc Id
                    </Typography>
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#0f172a", wordBreak: "break-all" }}>
                      {getFieldValue("vpcId")}
                    </Typography>
                  </Grid>

                  {/* Subnet */}
                  <Grid
                    size={{ xs: 12, md: 2 }}
                    sx={{ pl: { md: 2 } }}
                  >
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600, mb: 0.5 }}>
                      Subnet
                    </Typography>
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#0f172a", wordBreak: "break-all" }}>
                      {getFieldValue("subnet")}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>

          {!isActiveVMRequest && (
            <Box
              sx={{
                bgcolor: "#fff7ed",
                color: "#c2410c",
                p: 1.5,
                borderRadius: 2,
                mb: 2,
                display: "flex",
                gap: 1.5,
                border: "1px solid #fdba74",
                alignItems: "center",
              }}
            >
              <ErrorOutlineOutlined fontSize="small" />
              <Typography variant="body2" fontSize="0.85rem" fontWeight="500">
                Please select the maintenance window during which the VM can be resized.<strong> Make sure the required Gsnow (ServiceNow) approval is received before the selected maintenance time. </strong> If approval is not received before the maintenance window, the resize request will be cancelled.
              </Typography>
            </Box>
          )}

          {!isActiveVMRequest && (
            <Grid container spacing={2} sx={{ mb: 2, }} alignItems="stretch">

              <Grid size={{ xs: 12, md: 8 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Card
                      sx={{
                        border: 'none', p: 0, boxShadow: 'none', bgcolor: "#fafafa",
                        //  mt: 1.5
                        height: "100%"

                      }}
                    >
                      <CardContent>
                        <Grid size={{ xs: 12 }}>
                          {/* Header Section */}
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="subtitle1" fontWeight="700" color="#1e293b">
                              Current Configuration
                            </Typography>
                            <LockOutlined sx={{ color: "#94a3b8", fontSize: 20 }} />
                          </Box>

                          {/* Specs Section */}
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                              {/* Instance Type */}
                              <Box sx={{
                                bgcolor: "#f8fafc",
                                p: 1,
                                px: 1.5,
                                borderRadius: 2,
                                border: "1px solid #e2e8f0",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                height: "100%", // Ensures consistent height
                                boxSizing: "border-box"
                              }}>
                                <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", fontWeight: 600 }}>
                                  Instance Type
                                </Typography>
                                <Typography variant="body1" fontWeight="700" color="#2563eb">
                                  {currentInstanceType}
                                </Typography>
                              </Box>
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                              <Box sx={{ display: "flex", gap: 2, height: "100%" }}>
                                {/* vCPU */}
                                <Box sx={{
                                  flex: 1,
                                  bgcolor: "#f8fafc",
                                  p: 1,
                                  px: 1.5,
                                  borderRadius: 2,
                                  border: "1px solid #e2e8f0",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center"
                                }}>
                                  <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", fontWeight: 600 }}>
                                    vCPU
                                  </Typography>
                                  <Typography variant="body1" fontWeight="700" color="#334155">
                                    {currentCpu}
                                  </Typography>
                                </Box>

                                {/* RAM */}
                                <Box sx={{
                                  flex: 1,
                                  bgcolor: "#f8fafc",
                                  p: 1,
                                  px: 1.5,
                                  borderRadius: 2,
                                  border: "1px solid #e2e8f0",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center"
                                }}>
                                  <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", fontWeight: 600 }}>
                                    RAM
                                  </Typography>
                                  <Typography variant="body1" fontWeight="700" color="#334155">
                                    {currentRam}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>
                      </CardContent>

                      <CardContent sx={{ borderTop: "1px dashed #e2e8f0", p: 2, pt: 2, pb: 1, mt: 1 }}>
                        <Typography variant="subtitle1" fontWeight="700" color="#0f172a" mb={2}>
                          Target Configuration
                        </Typography>

                        <Grid container spacing={2} alignItems="flex-end">
                          {/* Left Side: Dropdown */}
                          
<Grid size={{ xs: 12, md: 6 }}>
  <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600, mb: 0.5 }}>
    Select New Instance Type
  </Typography>
  
  <Autocomplete
    fullWidth
    size="small"
    options={instanceTypes.length > 0 ? instanceTypes : [{ value: "t2.micro", text: "t2.micro | 1 vCPU | 1 GiB" }]}
    // Map the selected value back to the full option object
    value={instanceTypes.find((item) => item.value === selectedInstance) || (selectedInstance ? { value: selectedInstance, text: selectedInstance } : null)}
    onChange={(event, newValue) => {
      setSelectedInstance(newValue ? newValue.value : "");
    }}
    disabled={loadingTypes || isActiveVMRequest}
    // Disable selection if it matches the current instance type
    getOptionDisabled={(option) => {
      const itemInstanceName = option.text?.split("|")[0]?.trim().toLowerCase();
      return itemInstanceName === currentInstanceType?.toLowerCase();
    }}
    // Define what value is used to match options
    getOptionLabel={(option) => option.text || option.value}
    isOptionEqualToValue={(option, value) => option.value === value.value}
    
    // Add slotProps to control Popper placement and width
    slotProps={{
      popper: {
        sx: {
          // Ensures the dropdown matches the width of the input
          width: "100%",
          // Optional: add a slight offset if needed
          mt: 0.5, 
        },
        modifiers: [
          {
            name: 'flip',
            enabled: true,
          },
          {
            name: 'preventOverflow',
            enabled: true,
          },
        ],
      },
    }}

    renderInput={(params) => (
      <TextField
        {...params}
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        InputProps={{
          ...params.InputProps,
          startAdornment: (
            <>
              {loadingTypes ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
              {params.InputProps.startAdornment}
            </>
          ),
        }}
      />
    )}
    // Custom render option to append "(Current)" just like your MenuItem logic
    renderOption={(props, option) => {
      const itemInstanceName = option.text?.split("|")[0]?.trim().toLowerCase();
      const isCurrentType = itemInstanceName === currentInstanceType?.toLowerCase();

      // Extract key to pass separately from other list props
      const { key, ...otherProps } = props;

      return (
        <li key={key} {...otherProps}>
          {option.text || option.value} {isCurrentType && "(Current)"}
        </li>
      );
    }}
  />
</Grid>

                          {/* Right Side: Stats or Error Message */}
                          <Grid size={{ xs: 12, md: 6 }}>
                            {isSameInstance ? (
                              <Box
                                sx={{
                                  bgcolor: "#ffe4e6",
                                  color: "#be123c",
                                  p: 1.5,
                                  borderRadius: 2,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                  height: "40px",
                                }}
                              >
                                <ErrorOutlineOutlined fontSize="small" />
                                <Typography variant="body2" fontSize="0.75rem" fontWeight="500">
                                  Current and target instance types cannot be the same.
                                </Typography>
                              </Box>
                            ) : (
                              <Box sx={{ display: "flex", gap: 2 }}>
                                <Box
                                  sx={{
                                    flex: 1,
                                    bgcolor: "#f8fafc",
                                    p: 1,
                                    px: 1.5,
                                    borderRadius: 2,
                                    border: "1px solid #e2e8f0",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center"
                                  }}
                                >
                                  <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", fontWeight: 600 }}>
                                    vCPU
                                  </Typography>
                                  <Typography fontWeight="700" color={pricing ? "#334155" : "#cbd5e1"}>
                                    {loadingPricing ? <Skeleton width={30} /> : pricing?.vCPUs || "-"}
                                  </Typography>
                                </Box>

                                {/* RAM Box */}
                                <Box
                                  sx={{
                                    flex: 1,
                                    bgcolor: "#f8fafc",
                                    p: 1,
                                    px: 1.5,
                                    borderRadius: 2,
                                    border: "1px solid #e2e8f0",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center"
                                  }}
                                >
                                  <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", fontWeight: 600 }}>
                                    RAM
                                  </Typography>
                                  <Typography fontWeight="700" color={pricing ? "#334155" : "#cbd5e1"}>
                                    {loadingPricing ? <Skeleton width={40} /> : pricing?.ramGiB ? `${pricing.ramGiB} GiB` : "-"}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Grid>
                        </Grid>

                        {/* --- SCHEDULING SECTION --- */}
                        <Box sx={{ borderTop: "1px dashed #e2e8f0", pt: 3, mt: 2 }}>
                          <Box display="flex" gap={2} flexWrap="wrap" justifyContent="space-between">
                            {/* Date Picker */}
                            <Box sx={{ width: '49%', flexShrink: 0 }}>
                              <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DateTimePicker
                                  label="Scheduled Date & Time"
                                  value={maintenanceWindow ? new Date(maintenanceWindow) : null}
                                  onChange={(value) => setMaintenanceWindow(value ? value.toISOString() : "")}
                                  onError={(error) => {
                                    setDateError(!!error);
                                  }}
                                  minDateTime={getMinDateTime()}
                                  disablePast
                                  minutesStep={5}
                                  shouldDisableTime={(timeValue, view) => {
                                    if (!timeValue) return false;
                                    const now = new Date();
                                    const target = new Date(timeValue);

                                    // If it's today, disable past hours and past minutes
                                    if (target.toDateString() === now.toDateString()) {
                                      if (view === "hours") {
                                        return target.getHours() < now.getHours();
                                      }
                                      if (view === "minutes") {
                                        if (target.getHours() === now.getHours()) {
                                          return target.getMinutes() < now.getMinutes();
                                        }
                                      }
                                    }
                                    return false;
                                  }}
                                  disabled={isActiveVMRequest}
                                  slotProps={{
                                    textField: {
                                      fullWidth: true,
                                      required: true,
                                      size: "small",
                                      sx: { "& .MuiOutlinedInput-root": { borderRadius: 2 } },
                                    },
                                    actionBar: { actions: ["cancel", "accept"] },
                                    desktopPaper: { sx: { minWidth: "600px" } },
                                    popper: { sx: { zIndex: 9999 } },
                                  }}
                                />

                              </LocalizationProvider>
                            </Box>
                            <RoleGuard requiredPermission="VM_RESIZE">

                            <Button
                              variant="contained"
                              disableElevation
                              onClick={handleSave}
                              disabled={
                                !canResizeInstance ||
                                !selectedInstance ||
                                isSameInstance ||
                                !maintenanceWindow ||
                                dateError ||
                                hasNoPricing ||
                                loadingPricing ||
                                isActiveVMRequest ||
                                isSaving
                              }
                              sx={{
                                px: 4,
                                bgcolor: "#00b023",
                                color: "#fff",
                                height: "40px",
                                borderRadius: 1,
                                textTransform: "none",
                                fontWeight: 600,
                                "&:hover": {
                                  bgcolor: "#00911c",
                                },
                              }}
                            >
                              {isSaving ? "Submitting Request..." : "Submit Resize Request"}
                            </Button>
                            </RoleGuard>
                          </Box>
                          {/* <Box
                            sx={{
                              bgcolor: "#f0f9ff",
                              border: "1px solid #bae6fd",
                              borderRadius: 2,
                              p: 1.5,
                              mt: 2,
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <InfoOutlined sx={{ color: "#0284c7", fontSize: 20, flexShrink: 0 }} />
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#0369a1",
                                fontSize: "0.8rem",
                                fontWeight: 500,
                                flex: 1,
                              }}
                            >
                              Ensure the required <strong>Gsnow (ServiceNow ) </strong>  approval is received before the scheduled
                            </Typography>
                          </Box> */}
                        </Box>

                        {!canResizeInstance && (
                          <Box
                            sx={{
                              bgcolor: "#f8fafc",
                              color: "#2563eb",
                              p: 1.5,
                              borderRadius: 2,
                              mt: 2,
                              display: "flex",
                              gap: 1.5,
                            }}
                          >
                            <ErrorOutlineOutlined fontSize="small" />
                            <Typography variant="body2" fontSize="0.75rem" fontWeight="500">
                              To submit a VM Resize request, the EC2 instance must be in Running or Stopped state and have a valid AWS Instance ID.
                            </Typography>
                          </Box>
                        )}

                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>



                <Card
                  sx={{
                    background: (t) =>
                      t.palette.mode === "dark"
                        ? t.palette.background.paper
                        : "linear-gradient(145deg, #f0f7ff 0%, #e3f2fd 100%)",
                    border: 1,
                    borderColor: (t) =>
                      t.palette.mode === "dark"
                        ? "divider"
                        : "#e0eaf5",
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.03)",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column"
                  }}
                >

                  <CardContent
                    // sx={{ p: 2, flexGrow: 1 }}
                    sx={{
                      p: 2,
                      "&:last-child": {
                        paddingBottom: 0,
                      },
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column"
                    }}
                  >


                    <Typography variant="subtitle1" fontWeight="700" color="#0f172a">
                      Pricing Forecast
                    </Typography>
                    <Typography variant="body2" color="#64748b" mb={2} sx={{ fontSize: "0.8rem", fontWeight: 500 }}>
                      Estimated Cost Impact
                       {/* (new monthly and hourly rates without discount) */}
                    </Typography>

                    {/* Hourly Rates */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color: "#64748b",
                            fontWeight: 600,
                          }}
                        >
                          Current Hourly Rate
                        </Typography>

                        <Typography
                          variant="h6"
                          fontWeight={700}
                          color="#334155"
                        >
                          ${currentHourlyPrice}
                        </Typography>
                      </Box>

                      <Box sx={{ textAlign: "right" }}>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color: "#64748b",
                            fontWeight: 600,
                          }}
                        >
                          New Hourly Rate
                        </Typography>

                        {/* <Typography
      variant="h6"
      fontWeight={700}
      color="#2563eb"
    >
      ${newHourlyPrice.toFixed(3)}
    </Typography> */}
                        {selectedInstance ? (
                          loadingPricing ? (
                            <Box display="flex" justifyContent="flex-end"><Skeleton width={70} /></Box>
                          ) : (
                            <Typography variant="body1" fontWeight="700" color="#2563eb" textAlign="right">
                              ${newHourlyPrice.toFixed(3)}
                            </Typography>
                          )
                        ) : (
                          <Typography color="#94a3b8" textAlign="right">—</Typography>
                        )}
                      </Box>
                    </Box>


                    {/* Monthly Rates */}
                    <Box
                      sx={{
                        mb: 2,
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 6 }}>
                          <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600, mb: 0.5 }}>
                            Current Monthly Rate
                          </Typography>
                          <Typography variant="body1" fontWeight="700" color="#334155">
                            ${currentMonthlyPrice.toFixed(2)}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600, textAlign: "right", mb: 0.5 }}>
                            New Monthly Rate
                          </Typography>
                          {selectedInstance ? (
                            loadingPricing ? (
                              <Box display="flex" justifyContent="flex-end"><Skeleton width={70} /></Box>
                            ) : (
                              <Typography variant="body1" fontWeight="700" color="#2563eb" textAlign="right">
                                ${newPrice.toFixed(2)}
                              </Typography>
                            )
                          ) : (
                            <Typography color="#94a3b8" textAlign="right">—</Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Monthly Projected */}
                    <Box
                      sx={{
                        bgcolor: "#f4f7fc",
                        p: 2,
                        borderRadius: 2,
                        mb: 0.5,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      {newMonthlymarketprice ?
                      (<Typography variant="body2" fontWeight="600" color="#334155">
                        Monthly Projected 
                      </Typography>):(<Typography variant="body2" fontWeight="600" color="#334155">
                       Current Monthly Projected 
                      </Typography>)}

                      {/* {loadingPricing ? (
                        <Skeleton width={70} />
                      ) : (
                        <Typography variant="subtitle1" fontWeight="700" color="#0d7a46">
                          ${newMonthlyPrice.toFixed(2)} 
                        </Typography>
                      )} */}

                      {loadingPricing ? (
  <Skeleton width={70} />
) : newMonthlymarketprice ? (
  <Typography variant="subtitle1" fontWeight="700" color="#0d7a46">
    ${newMonthlymarketprice.toFixed(2)}
  </Typography>
) : (
  <Typography variant="subtitle1" fontWeight="700" color="#0d7a46">
                          ${currentMonthlyPrice.toFixed(2)} 
                        </Typography>
)}
                    </Box>

                    {/* Cost Delta */}
                  {selectedInstance &&  <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 2, p: 2, background: "#fff" }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" >
                        <Typography variant="body2" fontWeight="600" color={deltaColor}>
                          {deltaLabel}
                        </Typography>
                        {loadingPricing ? (
                          <Skeleton width={70} />
                        ) : (
                          <Typography variant="body2" fontWeight="700" color={deltaColor}>
                            {priceDiff > 0 ? "+" : ""}
                            ${priceDiff.toFixed(2)}/mo
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ height: 6, bgcolor: "#e2e8f0", borderRadius: 3, overflow: "hidden" }}>
                        <Box
                          sx={{
                            height: "100%",
                            width: selectedInstance && priceDiff <= 0 ? "50%" : "100%",
                            bgcolor: selectedInstance && priceDiff > 0 ? "#fca5a5" : "#86efac",
                            borderRadius: 3,
                          }}
                        />
                      </Box>
                    </Box>}
                  </CardContent>
                </Card>
              </Grid>

            </Grid>
          )}
        </>
      )}

      {resizeHistory.length === 0 ? (
        <Box
          sx={{
            mb: 2,
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: (t) => t.palette.mode === 'dark' ? 'background.paper' : '#f5f5f5'
          }}
        >
          <Typography variant="h6" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            No existing VM Resize History configuration found
          </Typography>
        </Box>
      ) : (
        <VMResizeHistoryTable
          resourceId={resourceId}
          resourceOverview={resourceOverview}
        />
      )}

      {!canResizeInstance && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: "#eaf4ff",
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          <InfoOutlined sx={{ color: "#2196f3", fontSize: 22 }} />
          <Typography variant="body2" sx={{ color: "#0f4c81", fontWeight: 500 }}>
            Resource status is <strong>{status}</strong>. VM Resize cannot be performed.
          </Typography>
        </Box>
      )}

      <Dialog
        open={openValidationDialog}
        onClose={() => setOpenValidationDialog(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: { sx: { pb: 2, px: 1, pt: 1 } }
        }}
      >
        <DialogTitle sx={{ fontSize: "18px", fontWeight: "Bold" }}>
          Validation
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {validationReason}
          </Typography>

        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setOpenValidationDialog(false)}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );


};

export default InstanceResize;